import type { Rule } from "../types";
import { makeFiring, mean } from "../util";

const EVIDENCE = "plews_2013_hrv_guided_training";

/**
 * Fires when sleep HRV is below a 30-day rolling baseline by ≥ 8 ms for the
 * last 3 consecutive nights. Plews 2013: HRV-guided training (reduce intensity
 * when 7d-rolling HRV < baseline ± SD) outperforms fixed prescriptions.
 */
export const hrvDrop3dBelowBaseline: Rule = {
  id: "hrv_drop_3d_below_baseline",
  domain: "health.recovery",
  topics: ["hrv", "training_load"],
  evidence_refs: [EVIDENCE],
  title: () => "HRV trending below baseline",
  evaluate(ctx) {
    if (ctx.vitals.length < 10) return null;

    const sortedDesc = [...ctx.vitals].sort((a, b) => (a.date < b.date ? 1 : -1));
    const last3 = sortedDesc.slice(0, 3);
    const baselineWindow = sortedDesc.slice(3, 33);

    const last3Vals = last3.map((r) => r.hrv_during_sleep_ms ?? r.hrv_status_ms);
    if (last3Vals.some((v) => v == null)) return null;

    const baseline = mean(
      baselineWindow.map((r) => r.hrv_during_sleep_ms ?? r.hrv_status_ms),
    );
    if (baseline == null) return null;

    const drops = last3Vals.map((v) => baseline - (v as number));
    if (!drops.every((d) => d >= 8)) return null;

    const avgDrop = mean(drops) ?? 0;
    return makeFiring({
      rule_id: "hrv_drop_3d_below_baseline",
      now: ctx.now,
      tz: ctx.tz,
      severity: "warn",
      message: `HRV ${avgDrop.toFixed(1)}ms below baseline ${baseline.toFixed(0)}ms — three nights running. Consider an easy day.`,
      evidence_refs: [EVIDENCE],
      factors: [
        {
          name: "hrv_drop_avg_ms",
          value: Number(avgDrop.toFixed(1)),
          contribution: "consider easy day",
          evidence_ref: EVIDENCE,
        },
      ],
      target_surface: "health.recovery",
      target_action: { kind: "reduce_intensity", params: { pct: 20 } },
    });
  },
};
